import { useMemo } from 'react'
import type { ISbStoryData } from '@storyblok/js'
import { useStoryblokState } from '@storyblok/react'
import type { Page } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { buildMarketplaceBannerCarouselItemsFromStory } from '@percent/cause-dashboard/storyblok/product-offers/banners/buildMarketplaceBannerCarouselItemsFromStory'
import { buildProductOffersCategoriesFromStory } from '@percent/cause-dashboard/storyblok/product-offers/buildProductOffersCategoriesFromStory'
import { ProductOffersPageContent } from './ProductOffersPageContent'
import { STORYBLOK_RESOLVE_RELATIONS } from '@percent/cause-dashboard/storyblok/product-offers/useProductOffersPageStory'
import { useGfmBannerVariant } from '@percent/cause-dashboard/common/hooks/useGfmBannerVariant/useGfmBannerVariant'

export type ProductOffersPageStoryBridgeProps = {
  initialStory: ISbStoryData<Page>
}

/** Subscribes to Storyblok Visual Editor bridge for live updates in preview. */
export function ProductOffersPageStoryBridge({ initialStory }: Readonly<ProductOffersPageStoryBridgeProps>) {
  // Same relation path as CDN `resolve_relations` in useProductOffersPageStory.
  const bridged = useStoryblokState(initialStory, {
    resolveRelations: STORYBLOK_RESOLVE_RELATIONS
  })
  const story = bridged ?? initialStory
  const { isReady: isTileVariantReady, variant: tileVariant } = useGfmBannerVariant('product_tile')
  const categorizedProducts = useMemo(
    () => buildProductOffersCategoriesFromStory(story, isTileVariantReady ? tileVariant : null),
    [story, isTileVariantReady, tileVariant]
  )
  const { isReady: isGfmVariantReady, variant: gfmVariant } = useGfmBannerVariant('carousel')
  const bannerCarouselSlides = useMemo(
    () => (isGfmVariantReady ? buildMarketplaceBannerCarouselItemsFromStory(story, gfmVariant) : []),
    [story, isGfmVariantReady, gfmVariant]
  )

  return (
    <ProductOffersPageContent categorizedProducts={categorizedProducts} bannerCarouselSlides={bannerCarouselSlides} />
  )
}
